/**
 * john-mooring.js
 *
 * Variant switcher for the John Mooring hero (sections/main-product.liquid).
 * Deliberately self-contained — no imports from @theme/* — to match the
 * simpler "jm-" architecture used by snippets/variant-metafields.liquid and
 * snippets/variant-data-element.liquid, rather than the Section Rendering
 * API / Component base class used by Horizon's own <variant-picker>.
 *
 * On click of a [data-variant-select] button:
 *   1. Look up the clicked button's <variant-data> ancestor and call
 *      .getVariant(variantId) on it (see snippets/variant-data-element.liquid).
 *   2. Crossfade every [data-variant-meta] node on the page to the new value
 *      (quick crossfade, ~150-200ms — see --jm-swap-duration in
 *      assets/john-mooring.css, which this file reads from so the JS
 *      timeout and the CSS transition never drift apart). This includes the
 *      main product image (variant.image / variant.imageAlt), the Story
 *      section's two variant-driven tiles (variant.storyTile1/storyTile3 —
 *      see sections/product-story.liquid), and the Quote section's quote +
 *      attribution (variant.pullQuote / variant.title — see
 *      sections/product-quote.liquid), which all swap the same way as the
 *      text fields. Matching is done document-wide rather than scoped to
 *      the clicked <variant-data> root because sections/product-story.liquid
 *      renders its own separate <variant-data> instance further down the
 *      page — same window.variantData global, different DOM subtree.
 *   3. Set --accent on every <variant-data> element on the page (each
 *      swappable section defines its own) to the new variant's accent color.
 *   4. Toggle .active across [data-variant-select] buttons (scoped to the
 *      clicked root — the variant switcher only lives in the hero).
 *   5. Update #jm-product-form's hidden id input and Add to Cart button
 *      (text + disabled state), plus the sticky CTA's button (also
 *      root-scoped — these only exist in the hero).
 *   6. Dispatch a bubbling 'variant:update' CustomEvent
 *      (detail: { variantId, variant }) from the <variant-data> root.
 *
 * If window.variantData / <variant-data> aren't ready yet (script execution
 * order issue), getVariant() returns null and the click is a no-op rather
 * than throwing — see snippets/variant-data-element.liquid.
 */

(function () {
  // This file is a classic (non-module) script, and it's intentionally
  // <script src> tagged from inside THREE separate section templates
  // (main-product.liquid, product-story.liquid, product-quote.liquid) so
  // each one's own <variant-data> root works standalone. When more than
  // one of those sections lands on the same page (e.g. the homepage),
  // each <script> tag re-fetches and re-executes this whole file — unlike
  // ES modules, the browser does NOT dedupe execution of classic scripts
  // just because the URL matches. Without this guard, every
  // document-level listener below (click delegation, add-to-cart submit
  // handler, etc.) gets attached once per section instance, so a single
  // click can fire the same handler 2-3x — this was the cause of a single
  // Add to Cart click adding multiple quantities of the same item.
  if (window.__johnMooringLoaded) return;
  window.__johnMooringLoaded = true;

  const FALLBACK_SWAP_DURATION_MS = 180;

  /**
   * Reads --jm-swap-duration off a .jm-hero element so the JS timeout
   * always matches whatever assets/john-mooring.css currently sets it to.
   * @param {Element} hero
   * @returns {number} duration in milliseconds
   */
  function getSwapDuration(hero) {
    if (!hero) return FALLBACK_SWAP_DURATION_MS;
    const raw = getComputedStyle(hero).getPropertyValue('--jm-swap-duration').trim();
    if (!raw) return FALLBACK_SWAP_DURATION_MS;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return FALLBACK_SWAP_DURATION_MS;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  /**
   * Builds the spec-pill markup for .jm-specs, mirroring the Liquid loop in
   * sections/main-product.liquid (split on newlines, drop blanks, dot + text).
   * @param {string} specsValue - newline-separated spec text.
   * @returns {string} HTML for the pills.
   */
  function buildSpecsHTML(specsValue) {
    return specsValue
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((text) => {
        const span = document.createElement('span');
        span.textContent = text;
        return `<div class="jm-spec"><span class="jm-spec-dot" aria-hidden="true"></span>${span.innerHTML}</div>`;
      })
      .join('');
  }

  /**
   * Builds the markup for the main product image, mirroring the Liquid
   * image_tag output in sections/main-product.liquid's .jm-media-main.
   * @param {string} url - image URL (already sized, e.g. width: 1200).
   * @param {string} alt - accessible alt text.
   * @returns {string} HTML for the <img>.
   */
  function buildImageHTML(url, alt) {
    const safeAlt = document.createElement('span');
    safeAlt.textContent = alt || '';
    return `<img src="${url}" alt="${safeAlt.innerHTML}" loading="eager" class="jm-media-main-image">`;
  }

  /**
   * HTML-escapes a string via textContent round-trip (same trick used
   * inline by buildSpecsHTML/buildImageHTML above, pulled out here so
   * buildTileHTML doesn't have to repeat it three times).
   * @param {string} value
   * @returns {string} escaped text, safe to drop into innerHTML.
   */
  function escapeHTML(value) {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML;
  }

  /**
   * Builds the markup for one Story-section tile's inner content, mirroring
   * snippets/story-tile.liquid (icon div, label div, and an optional sub
   * div that's omitted entirely when blank — same as the Liquid `{% if sub
   * != blank %}` guard).
   * @param {{icon: string, label: string, sub: string}|null} tile - shape
   *   from snippets/variant-metafields.liquid's storyTile1/storyTile3.
   * @returns {string} HTML for the tile's content, or '' if tile is null.
   */
  function buildTileHTML(tile) {
    if (!tile) return '';
    const subHTML = tile.sub ? `<div class="jm-tile-sub">${escapeHTML(tile.sub)}</div>` : '';
    return (
      `<div class="jm-tile-icon" aria-hidden="true">${escapeHTML(tile.icon)}</div>` +
      `<div class="jm-tile-label">${escapeHTML(tile.label)}</div>${subHTML}`
    );
  }

  /**
   * Crossfades every [data-variant-meta="key"] node inside root to a new
   * value: fade out, swap content at the midpoint, fade back in.
   * @param {Element|Document} root - element/document to search within.
   * @param {string} key - the data-variant-meta value to match.
   * @param {string} html - new innerHTML for matching nodes.
   * @param {number} duration - crossfade duration in ms.
   */
  function swapMeta(root, key, html, duration) {
    const nodes = root.querySelectorAll(`[data-variant-meta="${key}"]`);
    if (!nodes.length) return;

    nodes.forEach((node) => node.classList.add('jm-is-swapping'));

    window.setTimeout(() => {
      nodes.forEach((node) => {
        node.innerHTML = html;
        node.classList.remove('jm-is-swapping');
      });
    }, duration);
  }

  /**
   * Applies a variant's data to one <variant-data> root: swaps text,
   * re-themes --accent, updates the form + Add to Cart + sticky CTA, and
   * dispatches 'variant:update'.
   * @param {Element} root - the <variant-data> element.
   * @param {string} variantId
   * @param {object} variant - shape from snippets/variant-metafields.liquid.
   */
  function applyVariant(root, variantId, variant) {
    const hero = root.querySelector('.jm-hero');
    const duration = getSwapDuration(hero);

    if (variant.eyebrow) {
      swapMeta(document, 'eyebrow', variant.eyebrow, duration);
    }
    swapMeta(document, 'title', variant.title, duration);
    swapMeta(document, 'short_description', variant.shortDescription, duration);
    if (variant.specs) {
      swapMeta(document, 'specs', buildSpecsHTML(variant.specs), duration);
    }
    swapMeta(document, 'price', variant.priceFormatted, duration);
    if (variant.image) {
      swapMeta(document, 'image', buildImageHTML(variant.image, variant.imageAlt), duration);
    }
    if (variant.storyHtml) {
      swapMeta(document, 'story_html', variant.storyHtml, duration);
    }
    swapMeta(document, 'story_tile_1', buildTileHTML(variant.storyTile1), duration);
    swapMeta(document, 'story_tile_3', buildTileHTML(variant.storyTile3), duration);
    if (variant.pullQuote) {
      // Curly quote marks are part of the swapped markup (not CSS
      // ::before/::after) so they match sections/product-quote.liquid's
      // initial server-rendered markup exactly on swap.
      swapMeta(document, 'quote', `“${escapeHTML(variant.pullQuote)}”`, duration);
    }
    // Attribution isn't its own metafield — built from `title` here the same
    // way sections/product-quote.liquid builds it server-side on first paint.
    swapMeta(document, 'quote_attr', `John Mooring — on the ${escapeHTML(variant.title)}`, duration);

    // Every section that needs re-theming defines its own <variant-data>
    // instance (main-product.liquid's hero, product-story.liquid's Story
    // section, etc.) — re-theme all of them in lockstep, not just `root`,
    // so --accent stays consistent across the whole page. Within each
    // instance --accent still cascades down to its own sibling elements
    // (accent band, sticky CTA, story eyebrow) the same way it always did.
    document.querySelectorAll('variant-data').forEach((el) => {
      el.style.setProperty('--accent', variant.accentColor);
    });

    root.querySelectorAll('[data-variant-select]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.variantId === String(variantId));
    });

    const idInput = root.querySelector('#jm-product-form [data-product-form-id]');
    if (idInput) idInput.value = variantId;

    const addToCartBtn = root.querySelector('#jm-product-form .jm-btn-primary');
    if (addToCartBtn) {
      addToCartBtn.disabled = !variant.available;
      addToCartBtn.textContent = variant.available
        ? `Add to Cart — ${variant.priceFormatted}`
        : 'Sold Out';
    }

    const stickyBtn = root.querySelector('.jm-sticky-cta-btn');
    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;
      stickyBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    if (typeof root.currentVariantId !== 'undefined') {
      root.currentVariantId = variantId;
    }

    root.dispatchEvent(
      new CustomEvent('variant:update', {
        bubbles: true,
        detail: { variantId, variant },
      })
    );
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-variant-select]');
    if (!btn || btn.disabled) return;

    const root = btn.closest('variant-data');
    if (!root || typeof root.getVariant !== 'function') return;

    const variantId = btn.dataset.variantId;
    const variant = root.getVariant(variantId);
    if (!variant) return;

    applyVariant(root, variantId, variant);
  });

  /**
   * Wires up each .jm-sticky-cta bar to appear once the main Add to Cart
   * button (inside .jm-cta-group, the product <form>) has scrolled out of
   * view above the viewport, and disappear again once it's back in view
   * (scrolling back up) OR once the site footer (.footer-content, the only
   * reliable selector Shopify's footer-group rendering gives us — see
   * sections/footer.liquid) has scrolled into view, so the bar doesn't sit
   * on top of the footer. Mirrors the pattern used by Horizon's own
   * assets/sticky-add-to-cart.js, but scoped to this section's own
   * .jm-sticky-cta / [data-sticky-cta] markup, which nothing else watches.
   *
   * One pair of observers per .jm-hero on the page (there can be more than
   * one instance of this section), each paired with the sticky bar that
   * lives inside the same <variant-data> root. Both observers write to the
   * same `state` object so either one flipping can hide the bar, but the
   * cta-group observer is the only one that can show it (the footer being
   * out of view isn't itself a reason to show the bar).
   */
  function initStickyCta() {
    if (!('IntersectionObserver' in window)) return;

    const footer = document.querySelector('.footer-content');

    document.querySelectorAll('.jm-hero').forEach((hero) => {
      const root = hero.closest('variant-data');
      const stickyBar = root && root.querySelector('[data-sticky-cta]');
      const ctaGroup = hero.querySelector('.jm-cta-group');
      if (!stickyBar || !ctaGroup) return;

      const state = { scrolledPastCta: false, footerVisible: false };
      const render = () => {
        stickyBar.hidden = !state.scrolledPastCta || state.footerVisible;
      };

      const ctaObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Only treat "scrolled past" as scrolled UPWARD past the button
            // (boundingClientRect above the viewport), not "hasn't reached
            // it yet" — so the bar doesn't flash on before the user gets there.
            state.scrolledPastCta = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            render();
          });
        },
        { threshold: 0 }
      );
      ctaObserver.observe(ctaGroup);

      if (footer) {
        const footerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              state.footerVisible = entry.isIntersecting;
              render();
            });
          },
          { threshold: 0 }
        );
        footerObserver.observe(footer);
      }
    });
  }

  /**
   * Fetches the latest cart, the same way assets/product-form.js's
   * #refreshCart does: prefer the page's <cart-items-component> (which
   * knows how to re-render itself / the drawer from the response), falling
   * back to a plain /cart.json fetch if that component isn't present.
   * @returns {Promise<object>} the cart object.
   */
  async function refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  /**
   * Submit handler for #jm-product-form. Replaces the form's default
   * full-page POST (which would navigate to /cart) with the same AJAX +
   * event flow Horizon's own assets/product-form.js uses: POST to
   * /cart/add.js, then dispatch a CartLinesUpdateEvent (from the
   * '@shopify/events' module, import-mapped in snippets/scripts.liquid) so
   * the theme's existing <cart-drawer-component> (assets/cart-drawer.js)
   * picks it up and opens itself — no direct reference to the drawer
   * needed, it already listens document-wide.
   *
   * Both the main "Add to Cart" button (inside the form) and the sticky
   * bar's button (form="jm-product-form", outside the form in the DOM)
   * trigger this same 'submit' event on the form, so one listener covers
   * both.
   * @param {SubmitEvent} event
   */
  async function handleAddToCartSubmit(event) {
    event.preventDefault();

    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const buttons = [
      ...form.querySelectorAll('button[type="submit"]'),
      ...document.querySelectorAll(`button[form="${form.id}"]`),
    ];
    buttons.forEach((btn) => {
      btn.disabled = true;
    });

    const formData = new FormData(form);
    const itemCount = Number(formData.get('quantity')) || 1;

    // Loading the '@shopify/events' module and constructing the event are
    // kept in their own try/catch, separate from the actual /cart/add.js
    // fetch below — so if anything about the event machinery breaks (e.g.
    // an unexpected payload shape), the item still gets added to the cart;
    // we just fall back to opening the drawer directly instead of relying
    // on cart-drawer.js's event listener.
    let events = null;
    let deferred = null;
    try {
      events = await import('@shopify/events');
      deferred = events.CartLinesUpdateEvent.createPromise();

      // Dispatch before the fetch resolves — cart-drawer.js (and the cart
      // icon/count) await event.promise, they don't need the line item
      // dispatched synchronously with the final cart state.
      form.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(formData.get('id')), quantity: itemCount }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[john-mooring] Could not dispatch CartLinesUpdateEvent:', error);
      events = null;
      deferred = null;
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        body: formData,
      }).then((res) => res.json());

      if (response.status) {
        console.warn('[john-mooring] Add to cart error:', response.message);
        if (events && deferred) {
          form.dispatchEvent(
            new events.CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          const ajaxCart = await refreshCart();
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { didError: true, items: ajaxCart.items, source: 'jm-product-form', sourceId: form.id, itemCount },
          });
        }
        return;
      }

      const ajaxCart = await refreshCart();

      if (events && deferred) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'jm-product-form',
            sourceId: form.id,
            itemCount,
            sections: response.sections,
            didError: false,
          },
        });
      }
    } catch (error) {
      console.warn('[john-mooring] Add to cart failed:', error);
      if (deferred) deferred.reject(error);
    } finally {
      buttons.forEach((btn) => {
        btn.disabled = false;
      });
    }
  }

  function initAddToCart() {
    const form = document.getElementById('jm-product-form');
    if (form) form.addEventListener('submit', handleAddToCartSubmit);
  }

  /**
   * Wires up the "Watch ornament video" button + its <dialog> player
   * (see sections/main-product.liquid). The button's data-video-url and
   * [hidden] state track the active variant's video (from
   * snippets/ornament-video-map.liquid, surfaced on window.variantData as
   * `.video`) — updated here on every 'variant:update' event dispatched by
   * applyVariant() above, the same event the rest of the crossfade swap
   * logic relies on.
   *
   * Single global-ID dialog/button pair (#jm-watch-video-btn,
   * #jm-video-dialog), matching the existing #jm-product-form /
   * #jm-main-product convention in this file — this section isn't built to
   * support more than one instance per page.
   */
  function initWatchVideo() {
    const btn = document.getElementById('jm-watch-video-btn');
    const dialog = /** @type {HTMLDialogElement|null} */ (document.getElementById('jm-video-dialog'));
    const video = /** @type {HTMLVideoElement|null} */ (document.getElementById('jm-video-dialog-video'));
    const closeBtn = document.getElementById('jm-video-dialog-close');
    if (!btn || !dialog || !video) return;

    function closeVideoDialog() {
      if (!dialog.open) return;
      dialog.close();
    }

    btn.addEventListener('click', () => {
      const url = btn.dataset.videoUrl;
      if (!url) return;

      video.src = url;
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.showModal();
      video.play().catch(() => {
        // Autoplay can still be blocked in some browsers even on a direct
        // user click (e.g. reduced-data mode) — the visible <video controls>
        // let the shopper hit play themselves, so this is a silent no-op.
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeVideoDialog);

    // Native <dialog> closes on Escape by itself, but not on a backdrop
    // click — a click that lands on the <dialog> element itself (rather
    // than bubbling up from a descendant like the <video>) means it hit the
    // ::backdrop, so treat that as "close".
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeVideoDialog();
    });

    // Single cleanup spot for both the Escape-key close and the explicit
    // closeVideoDialog() calls above (they all end up firing this native
    // 'close' event) — stop playback, reset scrub position, drop the src
    // so the video doesn't keep buffering/playing offscreen, and release
    // the scroll lock.
    dialog.addEventListener('close', () => {
      video.pause();
      video.currentTime = 0;
      video.removeAttribute('src');
      video.load();
      document.documentElement.removeAttribute('scroll-lock');
    });

    document.addEventListener('variant:update', (event) => {
      const variant = /** @type {CustomEvent} */ (event).detail?.variant;
      if (!variant) return;

      if (variant.video) {
        btn.dataset.videoUrl = variant.video;
        btn.hidden = false;
      } else {
        btn.dataset.videoUrl = '';
        btn.hidden = true;
        // Switching to a variant with no video while the dialog is already
        // open would otherwise leave the previous variant's video playing
        // behind a button that no longer references it.
        closeVideoDialog();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initStickyCta();
      initAddToCart();
      initWatchVideo();
    });
  } else {
    initStickyCta();
    initAddToCart();
    initWatchVideo();
  }
})();
